/**
 * Type-safe routing system using branded types.
 *
 * The branded Route type ensures that only values created by route builder
 * functions can be passed to navigation components, preventing hardcoded
 * strings from being used as routes.
 */

/**
 * Branded Route type - only route builders can create these.
 * Prevents arbitrary strings from being used as routes.
 *
 * This uses TypeScript's structural typing escape hatch: by adding a
 * unique symbol property, we create a type that looks like a string
 * at runtime but requires explicit casting at compile time.
 */
declare const __brand: unique symbol
export type Route = string & { readonly [__brand]: 'Route' }

/**
 * Creates a branded Route. Works two ways:
 *
 * 1. Static routes (function call):
 *    route('/admin/dashboard')
 *
 * 2. Dynamic routes (tagged template literal):
 *    route`/admin/applications/${appId}`
 *
 * When used as a tagged template, interpolated values are automatically
 * URL-encoded. Do NOT pre-encode values.
 *
 * @example
 * // Static
 * const dashboard = route('/admin/dashboard')
 *
 * // Dynamic - appId is auto-encoded
 * const appDetail = (appId: string) => route`/admin/applications/${appId}`
 */
export function route(path: string): Route
export function route(strings: TemplateStringsArray, ...values: (string | number)[]): Route
export function route(
    pathOrStrings: string | TemplateStringsArray,
    ...values: (string | number)[]
): Route {
    // Static route: route('/path')
    if (typeof pathOrStrings === 'string') {
        return pathOrStrings as Route
    }

    // Tagged template: route`/path/${param}`
    // Interpolated values are automatically URL-encoded
    const strings = pathOrStrings
    const result = strings.reduce((acc, str, i) => {
        const value = values[i]
        const encoded = value !== undefined ? encodeURIComponent(String(value)) : ''
        return acc + str + encoded
    }, '')

    return result as Route
}

/**
 * Creates a Route from an arbitrary string path.
 *
 * WARNING: This bypasses type-safety and should only be used for:
 * - Routes that cannot be pre-defined (truly dynamic paths)
 * - External URLs that need Route typing (prefer ExternalLink instead)
 * - Migration of legacy code where adding to routes.ts is impractical
 *
 * Prefer defining routes in `lib/routes/routes.ts` whenever possible.
 *
 * @example
 * // Only use when routes.ts definition is not possible
 * const dynamicPath = unsafeRoute(`/some/truly/dynamic/${path}`)
 */
export function unsafeRoute(path: string): Route {
    return path as Route
}
